import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { dashboardV2QueryKeys } from '../api/queryKeys';
import { MissingTimesheetShift, MissingTimesheetWorker } from '../api/types';

import useAuth from '@/hooks/useAuth';
import ResolveTimesheetModal from '@/routes/Agency/Job/WorkerTable/WorkerMobileView/ResolveTimesheetModal';
import { trackDashboardV2ActionTaken } from '@/util/vexo';

/**
 * The Missing Timesheet pencil's entry point into the AUTHORITATIVE Resolve
 * Timesheet workflow (GRAVY-10330).
 *
 * This file is an ADAPTER and nothing else. It holds no copy, no validation, no
 * step logic and no endpoint: all of that belongs to
 * `Job/WorkerTable/WorkerMobileView/ResolveTimesheetModal`, which Job Detail
 * opens too. Dashboard V2 shipped a simplified parallel flow twice and QA failed
 * it twice — the wording, the staged Dismiss steps, the required supporting
 * details, the 50% "No longer needed" credit notice, the worker card and the
 * dependability warning had all drifted from the real thing. A second
 * implementation cannot be kept in step by review, so there is only one.
 *
 * **If you find yourself adding UI here, you are re-creating the bug.** Change
 * the shared modal instead; Job Detail gets the fix at the same time.
 *
 * All this file does is translate one queue row into the props the modal wants,
 * and answer the two questions the Dashboard alone can answer:
 *
 *   * WHICH OCCURRENCE (`shiftId`). A queue row is a (worker, occurrence) pair,
 *     and the same worker can be missing a timesheet on several shifts of one
 *     job. Job Detail is single-shift and omits this, so the server falls back
 *     to the job's first shift — right there, wrong here.
 *   * WHAT "DONE" MEANS (`onCompleted`). Job Detail reloads the browser because
 *     it IS the page. Doing that here would discard the tab, filters, page and
 *     scroll position the ticket requires be preserved, so the dashboard's
 *     queries are invalidated instead and the queue re-renders in place.
 */

type Props = {
  shift: MissingTimesheetShift;
  worker: MissingTimesheetWorker;
  onClose: () => void;
};

const QueueResolveTimesheet = ({ shift, worker, onClose }: Props) => {
  const queryClient = useQueryClient();
  const { currentAdmin, isCurrentCustomerAdmin } = useAuth();
  // Guarded on null — `isCurrentCustomerAdmin` reads `.customer` and throws on
  // an admin that has not loaded yet (the same guard NoShowShiftCard uses).
  const clientAdmin = !!currentAdmin && isCurrentCustomerAdmin(currentAdmin);

  const name = worker.name || 'this worker';

  /**
   * The modal reads `job.id`, `job.status` and `job.address.timezone`, and
   * nothing else. `status` is hardcoded COMPLETED rather than guessed: a shift
   * only enters this queue once it has ENDED without a timesheet, so the job is
   * complete by construction — and the modal gates Dismiss Worker on exactly
   * that, so an absent status would silently hide one of the three actions.
   */
  const job = useMemo(
    () => ({
      id: shift.job_id,
      status: 'COMPLETED',
      address: { timezone: shift.timezone },
    }),
    [shift.job_id, shift.timezone],
  );

  /**
   * `workerInfo.id` is the WORKER id, not the job_worker id — that is what both
   * `resolve_enter_hours` and `dismiss` take, and sending the wrong one would
   * 404 against an authorized job.
   *
   * The rating drives the worker card on the Dismiss confirmation step; the
   * queue payload carries it (added for this ticket) so the card is not a blank
   * where Job Detail shows identity and rating.
   */
  const workerInfo = useMemo(
    () => ({
      id: worker.worker_id,
      avatarUrl: worker.avatar_url ?? undefined,
      averageRating: worker.average_rating ?? undefined,
      jobsRatingCount: worker.jobs_rating_count ?? undefined,
      user: {
        firstName: name.split(' ')[0],
        lastName: name.split(' ').slice(1).join(' '),
      },
    }),
    [
      worker.worker_id,
      worker.avatar_url,
      worker.average_rating,
      worker.jobs_rating_count,
      name,
    ],
  );

  /**
   * Whatever the queue captured, in the shape Enter Hours prefills from. A row
   * in this queue has no timesheet by definition, so these are usually null —
   * but a partially captured one (check-in, no check-out) must still prefill,
   * exactly as it does on Job Detail.
   */
  const timesheet = useMemo(
    () =>
      ({
        id: worker.timesheet_id ?? undefined,
        checkinAt: worker.check_in ?? null,
        checkoutAt: worker.check_out ?? null,
        reportedCheckinAt: worker.check_in ?? null,
        reportedCheckoutAt: worker.check_out ?? null,
        reportedBreakMinutes: worker.break_minutes ?? null,
      }) as never,
    [
      worker.timesheet_id,
      worker.check_in,
      worker.check_out,
      worker.break_minutes,
    ],
  );

  return (
    <ResolveTimesheetModal
      open
      onClose={onClose}
      workerFullName={name}
      // This queue is missing TIMESHEETS, so the modal's description is the
      // missing-timesheet one. Passing both flags would pick the check-out copy.
      hasMissingCheckout={false}
      hasMissingTimesheet
      isClientAdmin={clientAdmin}
      timesheet={timesheet}
      job={job}
      workerInfo={workerInfo}
      shiftId={shift.shift_id}
      onCompleted={() => {
        // Every dashboard query, not just this queue: resolving a worker can
        // empty a shift out of Missing Timesheet, move them into No Show, and
        // change the badge counts on tabs the admin is not looking at.
        void queryClient.invalidateQueries({
          queryKey: dashboardV2QueryKeys.root,
        });
        onClose();
        // LAST, and deliberately so: measuring an action must never be able to
        // break it. Anything that throws here — a partially mocked module, a
        // future transport change — would otherwise take the refetch and the
        // close with it, so the admin's resolution appears not to have worked.
        //
        // Fires only on completion, so a modal opened and cancelled is not
        // counted as work done. `onCompleted` takes no argument, so the modal's
        // branches (Enter Hours / Dismiss / no-show) cannot be told apart here.
        trackDashboardV2ActionTaken({
          action: 'resolve_timesheet',
          tab: 'missing_timesheet',
          shift_id: shift.shift_id,
          worker_id: worker.worker_id,
        });
      }}
    />
  );
};

export default QueueResolveTimesheet;
